import React, { useMemo, useState, useCallback } from 'react';
import { Button, Stack, Flex, spacing, useTheme } from '@fti/design-system';
import type { DatasetWithPreview } from '../../types/dataset';
import type { ColumnType, TypeAssignment } from '../../types/columnType';
import { ColumnTypeBadge } from '../ColumnTypeSystem/ColumnTypeBadge';
import { TypeSelector } from '../ColumnTypeSystem/TypeSelector';
import { IncompatibilityWarning } from '../ColumnTypeSystem/IncompatibilityWarning';
import { inferColumnTypes, extractColumnsFromRows } from '../../utils/typeInference';
import { checkCompatibility } from '../../utils/typeValidation';
import type { ChartValidationResult } from '../../types/columnType';
import { validateChartFields } from '../../utils/chartTypeCompatibility';
import type { ChartConfig } from '../../utils/chartTypeCompatibility';

interface DataPreviewProps {
  dataset: DatasetWithPreview;
  charts?: ChartConfig[];
  readOnly?: boolean;
  onTypesChanged?: (columnTypes: ColumnType[]) => void;
  onValidationResults?: (results: ChartValidationResult[]) => void;
}

export const DataPreview: React.FC<DataPreviewProps> = ({
  dataset,
  charts = [],
  readOnly = false,
  onTypesChanged,
  onValidationResults,
}) => {
  const { theme } = useTheme();

  const inferredTypes = useMemo(() => {
    const columnData = extractColumnsFromRows(dataset.preview_rows, dataset.columns.length);
    return inferColumnTypes(columnData, dataset.columns);
  }, [dataset.preview_rows, dataset.columns]);

  const [columnTypes, setColumnTypes] = useState<ColumnType[]>(() => {
    if (dataset.column_types && dataset.column_types.length > 0) {
      return dataset.column_types.map((ct) => ct.column_type);
    }
    return inferredTypes;
  });

  const [savedTypes, setSavedTypes] = useState<ColumnType[]>(() => [...columnTypes]);

  const hasUnsavedChanges = useMemo(
    () => columnTypes.some((t, i) => t !== savedTypes[i]),
    [columnTypes, savedTypes]
  );

  const [validationResults, setValidationResults] = useState<ChartValidationResult[]>([]);
  const [history, setHistory] = useState<TypeAssignment[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [openSelectorIndex, setOpenSelectorIndex] = useState<number | null>(null);

  const [pendingChange, setPendingChange] = useState<{
    columnIndex: number;
    targetType: ColumnType;
    incompatibleCount: number;
    totalCount: number;
  } | null>(null);

  const displayRows = dataset.preview_rows.slice(0, 10);

  const applyTypeChange = useCallback(
    (columnIndex: number, newType: ColumnType) => {
      const previousType = columnTypes[columnIndex];
      const assignment: TypeAssignment = {
        columnIndex,
        previousType,
        newType,
        timestamp: Date.now(),
      };

      const newHistory = history.slice(0, historyIndex + 1);
      newHistory.push(assignment);
      setHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);

      setColumnTypes((prev) => {
        const updated = [...prev];
        updated[columnIndex] = newType;
        return updated;
      });
    },
    [columnTypes, history, historyIndex]
  );

  const handleTypeChangeRequest = useCallback(
    (columnIndex: number, newType: ColumnType) => {
      setOpenSelectorIndex(null);

      if (newType === columnTypes[columnIndex]) return;

      const columnData = extractColumnsFromRows(dataset.preview_rows, dataset.columns.length);
      const result = checkCompatibility(columnData[columnIndex], newType);

      if (result.incompatible > 0) {
        setPendingChange({
          columnIndex,
          targetType: newType,
          incompatibleCount: result.incompatible,
          totalCount: result.total,
        });
      } else {
        applyTypeChange(columnIndex, newType);
      }
    },
    [columnTypes, dataset.preview_rows, dataset.columns.length, applyTypeChange]
  );

  const handleConfirmChange = useCallback(() => {
    if (pendingChange) {
      applyTypeChange(pendingChange.columnIndex, pendingChange.targetType);
      setPendingChange(null);
    }
  }, [pendingChange, applyTypeChange]);

  const handleSave = useCallback(() => {
    setSavedTypes([...columnTypes]);
    const results = validateChartFields(charts, columnTypes);
    setValidationResults(results);
    onTypesChanged?.(columnTypes);
    onValidationResults?.(results);
  }, [columnTypes, charts, onTypesChanged, onValidationResults]);

  const handleDiscard = useCallback(() => {
    setColumnTypes([...savedTypes]);
    setHistory([]);
    setHistoryIndex(-1);
  }, [savedTypes]);

  // Undo/redo keyboard shortcuts
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const isMeta = e.metaKey || e.ctrlKey;
      if (isMeta && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        if (historyIndex >= 0) {
          const assignment = history[historyIndex];
          setColumnTypes((prev) => {
            const updated = [...prev];
            updated[assignment.columnIndex] = assignment.previousType;
            return updated;
          });
          setHistoryIndex(historyIndex - 1);
        }
      } else if (isMeta && e.key === 'z' && e.shiftKey) {
        e.preventDefault();
        if (historyIndex < history.length - 1) {
          const assignment = history[historyIndex + 1];
          setColumnTypes((prev) => {
            const updated = [...prev];
            updated[assignment.columnIndex] = assignment.newType;
            return updated;
          });
          setHistoryIndex(historyIndex + 1);
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [history, historyIndex]);

  return (
    <Stack gap="md">
      {/* Import warnings */}
      {dataset.import_warnings && dataset.import_warnings.length > 0 && (
        <div
          style={{
            padding: spacing.sm,
            borderRadius: '6px',
            backgroundColor: `${theme.colors.semantic.warning.light}20`,
            border: `1px solid ${theme.colors.semantic.warning.light}`,
            fontSize: '0.85rem',
            color: theme.colors.semantic.warning.default,
          }}
        >
          {dataset.import_warnings.map((warning, idx) => (
            <p key={idx} style={{ margin: idx > 0 ? `${spacing.xs} 0 0 0` : '0' }}>
              {warning.message}
            </p>
          ))}
        </div>
      )}

      {/* Save/Discard bar */}
      {hasUnsavedChanges && (
        <Flex justify="between" align="center" style={{ padding: `${spacing.xs} 0` }}>
          <span style={{ fontSize: '0.85rem', color: theme.colors.text.secondary }}>
            Unsaved type changes
          </span>
          <Flex gap="sm">
            <Button variant="outline" color="neutral" size="sm" onClick={handleDiscard}>
              Discard
            </Button>
            <Button variant="solid" color="primary" size="sm" onClick={handleSave}>
              Save
            </Button>
          </Flex>
        </Flex>
      )}

      {/* Table — matches DS Table styling with type badges in headers */}
      <div style={{ width: '100%', overflowX: 'auto' }}>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontFamily: 'inherit',
            fontSize: '1rem',
            color: theme.colors.text.primary,
          }}
          aria-label="Dataset preview"
        >
          <thead>
            <tr style={{ backgroundColor: theme.colors.neutral[50] }}>
              {dataset.columns.map((col, idx) => (
                <th
                  key={col}
                  style={{
                    padding: spacing.md,
                    textAlign: 'left',
                    fontWeight: 600,
                    color: theme.colors.text.primary,
                    borderBottom: `2px solid ${theme.colors.border.default}`,
                    whiteSpace: 'nowrap',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm }}>
                    <span>{col}</span>
                    <div style={{ position: 'relative' }}>
                      <ColumnTypeBadge
                        type={columnTypes[idx]}
                        onClick={readOnly ? undefined : () =>
                          setOpenSelectorIndex(openSelectorIndex === idx ? null : idx)
                        }
                      />
                      {!readOnly && openSelectorIndex === idx && (
                        <TypeSelector
                          currentType={columnTypes[idx]}
                          onTypeChange={(newType) => handleTypeChangeRequest(idx, newType)}
                          onClose={() => setOpenSelectorIndex(null)}
                        />
                      )}
                    </div>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayRows.map((row, rowIdx) => (
              <tr
                key={row.row_index}
                style={{
                  borderBottom: `1px solid ${theme.colors.border.default}`,
                  backgroundColor: rowIdx % 2 === 0 ? undefined : theme.colors.neutral[50],
                }}
              >
                {dataset.columns.map((col, colIdx) => (
                  <td
                    key={col}
                    style={{
                      padding: spacing.md,
                      verticalAlign: 'middle',
                      maxWidth: '240px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {row.values[colIdx] ?? ''}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p style={{ margin: 0, fontSize: '0.85rem', color: theme.colors.text.secondary }}>
        Showing first {displayRows.length} of {dataset.row_count.toLocaleString()} rows
      </p>

      {/* Chart validation results */}
      {validationResults.length > 0 && (
        <Stack gap="xs">
          {validationResults.map((result, idx) => (
            <div
              key={idx}
              style={{
                padding: spacing.sm,
                borderRadius: '6px',
                backgroundColor:
                  result.severity === 'error'
                    ? `${theme.colors.semantic.error.light}20`
                    : `${theme.colors.semantic.warning.light}20`,
                border: `1px solid ${
                  result.severity === 'error'
                    ? theme.colors.semantic.error.light
                    : theme.colors.semantic.warning.light
                }`,
                fontSize: '0.85rem',
                color:
                  result.severity === 'error'
                    ? theme.colors.semantic.error.default
                    : theme.colors.semantic.warning.default,
              }}
            >
              {result.message}
            </div>
          ))}
        </Stack>
      )}

      {/* Incompatibility warning modal */}
      {pendingChange && (
        <IncompatibilityWarning
          incompatibleCount={pendingChange.incompatibleCount}
          totalCount={pendingChange.totalCount}
          targetType={pendingChange.targetType}
          onConfirm={handleConfirmChange}
          onCancel={() => setPendingChange(null)}
        />
      )}
    </Stack>
  );
};
